import React, { useEffect } from 'react';
import { Navigate, Outlet, useOutletContext } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Spin } from 'antd';
import { ModernLayout } from '@/components/layout/ModernLayout';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';
import { initializeSound } from '@/utils/sound';

/**
 * ProtectedLayout
 *
 * Combines authentication check with ModernLayout.
 * Renders ModernLayout once with persistent sidebar/menu,
 * and uses Outlet for changing page content.
 *
 * This prevents menu from re-rendering on route changes,
 * eliminating flicker and preserving scroll position naturally.
 */

interface ProtectedLayoutProps {
  noPadding?: boolean;
}

type LayoutContextType = {
  setNoPadding: (value: boolean) => void;
};

export const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({
  noPadding: defaultNoPadding = false,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [noPadding, setNoPadding] = React.useState(defaultNoPadding);

  // Initialize WebSocket listener for notifications
  useNotificationSocket({ enabled: isAuthenticated });

  // Initialize sound system on first user interaction
  useEffect(() => {
    const initSound = () => {
      initializeSound();
      // Remove listeners after first interaction
      document.removeEventListener('click', initSound);
      document.removeEventListener('keydown', initSound);
    };

    document.addEventListener('click', initSound, { once: true });
    document.addEventListener('keydown', initSound, { once: true });

    return () => {
      document.removeEventListener('click', initSound);
      document.removeEventListener('keydown', initSound);
    };
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Render ModernLayout once with Outlet for child routes
  return (
    <ModernLayout noPadding={noPadding}>
      <Outlet context={{ setNoPadding } satisfies LayoutContextType} />
    </ModernLayout>
  );
};

/**
 * Hook for child pages to set noPadding option
 */
export function useLayoutConfig() {
  return useOutletContext<LayoutContextType>();
}
